import { useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { router, usePage } from "@inertiajs/react";
import { useAdStudioRole } from "@/Hooks/useAdStudioRole";

import { useColorScheme, useDisclosure } from "@mantine/hooks";
import {
  ActionIcon,
  Box,
  Button,
  CopyButton,
  Flex,
  Grid,
  Group,
  Loader,
  Modal,
  Paper,
  Skeleton,
  Text,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconCopy,
  IconLink,
  IconMaximize,
  IconShare3,
  IconAlignBoxCenterMiddle,
  IconCarouselHorizontal,
  IconCube,
  IconVideo,
  IconUserCircle,
  IconHandMove,
  IconNotes,
  IconDeviceMobile,
  IconRefresh,
  IconPencil,
} from "@tabler/icons-react";
import { StagingEnvWrapper } from "@/lib";
import { BilloardIcon, HalfpageIcon, MediumRectangleIcon } from "@/Components/ui/icons/FormatIcons";
import { actionsTemplateCard, mutateAdUpdate, queryOptions, queryShowcases } from "@/Services";
import { SvgTemplateBillboard } from ".";
import { SvgPreviewHalfpage, SvgPreviewMedium } from "./SvgPreview";
import classes from "./TemplateCard.module.css";
import { useTranslation } from "react-i18next";

const ScratchIcon = ({ size = 16, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
  <path d="M11.25 5.36438L15.1153 1.5L16.5 2.88469L12.6347 6.74907" stroke="currentColor" stroke-linejoin="round"/>
  <path d="M9.74981 12L7.73246 16.0346C7.61801 16.2636 7.3129 16.3131 7.13191 16.1321L5.62481 14.625M5.62481 14.625L3.37481 12.375M5.62481 14.625L6.74835 12.75M5.99981 8.25L1.96517 10.2674C1.73623 10.3818 1.68672 10.6869 1.86771 10.8679L3.37481 12.375M3.37481 12.375L5.24981 11.25" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M13.1261 7.125L10.8761 4.875L9.00146 6.75L5.56886 7.09349C5.44296 7.10609 5.38826 7.2593 5.4777 7.34879L10.6526 12.5261C10.7421 12.6156 10.8954 12.5609 10.908 12.4349L11.2515 9L13.1261 7.125Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
);

const HIGH_IMPACT_MAP = {
  1: "Carousel",
  2: "Story",
  3: "Video",
  4: "Swipe",
  5: "Native",
  6: "Scratch",
  7: "Post",
  8: "Super-Light",
  9: "Promotion Slider",
  10: "Cube",
};



function getTemplateHighImpact(src) {
  const t =
    src?.template ||
    src?.adShowcase?.template ||
    src?.ad_showcase?.template ||
    src?.showcase?.template ||
    null;

  const isScratch =
    src?.is_scratch ||
    src?.adShowcase?.is_scratch ||
    src?.ad_showcase?.is_scratch ||
    src?.showcase?.is_scratch;

  const idRaw = isScratch
    ? 6
    : src?.adShowcase?.high_impact_format ??
    src?.ad_showcase?.high_impact_format ??
    src?.showcase?.high_impact_format ??
    t?.high_impact_format;
  const id = idRaw == null || idRaw === "" ? null : Number(idRaw);
  return { id, label: id ? HIGH_IMPACT_MAP[id] ?? "" : "" };
}

const HI_ICON = {
  Carousel: (props) => <IconCarouselHorizontal stroke={2} size={16} {...props} />,
  Story: (props) => <IconUserCircle stroke={2} size={16} {...props} />,
  Cube: (props) => <IconCube stroke={2} size={16} {...props} />,
  Video: (props) => <IconVideo stroke={2} size={16} {...props} />,
  Swipe: (props) => <IconHandMove stroke={2} size={16} {...props} />,
  Native: (props) => <IconNotes stroke={2} size={16} {...props} />,
  Scratch: (props) => <ScratchIcon stroke={2} size={16} {...props} />,
  Post: (props) => <IconDeviceMobile stroke={2} size={16} {...props} />,
  "Super-Light": (props) => <IconAlignBoxCenterMiddle stroke={2} size={16} {...props} />,
  "Promotion Slider": (props) => <IconCarouselHorizontal stroke={2} size={16} {...props} />,
  "Cube Ad": (props) => <IconCube stroke={2} size={16} {...props} />,
};

export const TemplateCard = ({
  id,
  image = null,
  name,
  ad_showcase: adShowcaseProp,
  hash = "",
  ctaText,
  ctaIcon = null,
  newEditor = false,
  phoneVariant = null,
  ...props
}) => {
  const { t } = useTranslation();
  const ad_showcase = adShowcaseProp ?? props?.ad_showcase ?? props?.adShowcase ?? {};
  const { data: formatData } = queryOptions({ type: "format" });


  const hi = useMemo(
    () => getTemplateHighImpact({ ad_showcase, ...props }),
    [ad_showcase, props]
  );
  const hiLabel = hi.label;
  const ctaTextComputed = useMemo(() => {
    if (hiLabel) return t('templates.createTypedAd', { type: hiLabel });
    return t('templates.createAd');
  }, [hiLabel, t]);

  const finalCtaText = ctaText ?? ctaTextComputed;

  const HiIcon = hiLabel ? HI_ICON[hiLabel] : null;

  const { createEmptyFromShowcase, createCloneFromShowcase } = actionsTemplateCard(id);
  // Until the new editor has every template: Carousel, Story and Promotion Slider open it, the rest the old one.
  const opensNewEditor = newEditor && [1, 2, 9].includes(hi.id);
  const createInNewEditor = () => router.visit(route("studio.studio_editor_v2.create", {
    template: hi.id === 2 ? "story" : "split-slider",
    format: { 1: "medium-rectangle", 2: "half-page", 3: "billboard", 4: "billboard" }[ad_showcase?.banner_format_id],
  }));
  const [opened, { open, close }] = useDisclosure(false);
  const [openedShare, { open: openShare, close: closeShare }] = useDisclosure(false);

  const { config } = usePage().props || {};
  const { isAdmin } = useAdStudioRole();

  const colorScheme = useColorScheme();



  const [imageSrc, setImageSrc] = useState(() => {
    const cacheId = props?.updated_at
      ? Date.parse(props.updated_at) || Date.now()
      : Date.now();
    let baseSrc = `${config?.adProcessingUrl || ""}${hash}?cacheId=${cacheId}`;
    if (route().params?.overwrite === "1") baseSrc += "&time_overwrite=timestamp";
    return baseSrc;
  });

  useEffect(() => {
    if (image) return;

    if (!imageSrc.includes("&time_overwrite=timestamp")) {
      isImageFullyWhite(imageSrc).then((isWhite) => {
        if (isWhite) setImageSrc((curr) => curr + "&time_overwrite=timestamp");
      });
    }
  }, [image, props?.updated_at, hash, config?.adProcessingUrl, route().params?.overwrite, imageSrc]);

  const isGif = image && /\.gif(\?|$)/i.test(image);

  const renderImagePreview = () => {
    const previewSrc = image || imageSrc;
    if (!previewSrc) return null;


    if (image && isGif) {
      return (
        <img
          src={image}
          alt={ad_showcase?.name || t('templates.highImpactPreview')}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      );
    }

    switch (ad_showcase.banner_format_id) {
      case 1:
        return <SvgPreviewMedium id={id} src={previewSrc} key={`image${id}`} />;
      case 2:
        return <SvgPreviewHalfpage id={id} src={previewSrc} key={`image${id}`} />;
      case 3:
        return <SvgTemplateBillboard id={id} src={previewSrc} key={`image${id}`} />;
      case 4:
        return <SvgTemplateBillboard id={id} src={previewSrc} key={`image${id}`} />;
      default:
        return null;
    }
  };

  const mutation = mutateAdUpdate({ adId: id });

  const refreshPreviewImage = () => {
    mutation.mutate({ banner_ids: [props?.ad_banner_formats?.[0]?.id], updateOnly: 1 }, {
      onSuccess: ({ data }) => {
        setImageSrc(data?.previewUrl);
      }
    });
  }



  const shareUrl = route("studio.templates", {
    filterBy: "format",
    shareId: id,
  });

  return (
    <Box className={classes.root} data-phone-variant={phoneVariant}>
      <Modal
        size="1400px"
        opened={opened}
        onClose={close}
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
        radius="lg"
        keepMounted
      >
        {opened && <TemplateCardPreview id={id} />}
      </Modal>


      <Modal
        title={t('templates.quickShare')}
        size="md"
        opened={openedShare}
        onClose={closeShare}
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
        radius="lg"
      >
        <Textarea radius="md" w="100%" value={shareUrl} autosize />
        <Flex mt="xs" gap="md">
          <CopyButton value={shareUrl}>
            {({ copied, copy }) => (
              <Button
                size="sm"
                color="smartico-pink"
                variant={copied ? "light" : "filled"}
                onClick={copy}
                radius="md"
                leftSection={<IconCopy size={16} />}
              >
                {copied ? t('templates.copiedUrl') : t('templates.copyUrl')}
              </Button>
            )}
          </CopyButton>
          <Button
            radius="md"
            size="sm"
            component="a"
            color="smartico-pink"
            target="_blank"
            href={shareUrl}
            leftSection={<IconLink />}
          >
            {t('templates.openNewWindow')}
          </Button>
        </Flex>
      </Modal>

      <Paper radius="lg" p={0} pos="relative" className={classes.previewPaper}>
        <StagingEnvWrapper taskId="AS-15">
          <ActionIcon
            onClick={openShare}
            className={classes.topActions}
            style={{ left: 10, top: 10, position: "absolute" }}
            variant={colorScheme === "dark" ? "filled" : "light"}
            color={colorScheme === "dark" ? "dark" : "gray"}
            radius="lg"
            aria-label={t('common.share')}
          >
            <IconShare3 style={{ width: "60%", height: "60%" }} stroke={2} />
          </ActionIcon>
        </StagingEnvWrapper>

        <ActionIcon
          onClick={open}
          className={classes.maximizeIcon}
          radius="lg"
          aria-label={t('templates.previewFullscreen')}
        >
          <IconMaximize stroke={2} size={18} />
        </ActionIcon>

        <div
          className={classes.previewFullscreenPill}
          onClick={open}
          role="button"
          aria-label={t('templates.previewFullscreen')}
        >
          <span>{t('templates.previewFullscreen')}</span>
          <span className={classes.pillIcon}>
            <IconMaximize size={16} />
          </span>
        </div>

        <Box className={classes.hoverOverlay}>
          <div className={classes.buttonWrapper}>
            <button className={classes.hoverButton} onClick={opensNewEditor ? createInNewEditor : createCloneFromShowcase}>
              {ctaIcon}
              <span>{finalCtaText}</span>
            </button>
          </div>
        </Box>

        <div className={classes.previewInner}>
          <Box className={classes.previewViewport} w="100%">
            {renderImagePreview()}
          </Box>
        </div>
      </Paper>

      <Group justify="space-between" mt="xs" align="start">
        <div>
          <Title
            order={5}
            fw="400"
            lineClamp={1}
            title={ad_showcase?.name}
            className={classes.adName}
            ta="left"
          >
            {ad_showcase?.name}
          </Title>

          <Group justify="flex-start" align="center" className={classes.formatRow}>
            <Group gap={6} align="center">
              {{
                1: <MediumRectangleIcon stroke={2} size={16} color="#343434" />,
                2: <HalfpageIcon box={24} color="#343434" />,
                3: <BilloardIcon box={24} color="#343434" />,
              }[ad_showcase?.banner_format_id] || null}

              <Text size="sm" className={classes.formatText} tt="capitalize">
                {
                  formatData
                    ?.find((item) => item.value == ad_showcase?.banner_format_id)
                    ?.label?.split(" (")[0]
                }
              </Text>
            </Group>

            {hiLabel ? <span className={classes.dotSep} aria-hidden="true" /> : null}

            {hiLabel ? (
              <Group gap={6} align="center" wrap="nowrap">
                {HiIcon ? <HiIcon /> : null}
                <Text size="sm" className={classes.formatText} style={{ whiteSpace: "nowrap" }}>
                  {hiLabel}
                </Text>
              </Group>
            ) : null}
            {
              (isAdmin && route().params?.mode == "dev") && (
                <Group gap={6}>
                  <Tooltip label={t('templates.refreshPreviewImage')}>
                    <ActionIcon loading={mutation.isPending} variant={colorScheme == 'dark' ? 'filled' : 'light'} color={colorScheme == 'dark' ? 'dark' : 'gray'} radius="lg" aria-label={t('templates.refreshPreview')} onClick={refreshPreviewImage}>
                      <IconRefresh stroke={2} size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <ActionIcon component={'a'} target="_blank" href={route('studio.ad_builder.edit', { ad: id })} variant={colorScheme == 'dark' ? 'filled' : 'light'} color={colorScheme == 'dark' ? 'dark' : 'gray'} radius="lg" aria-label={t('templates.maximize')}>
                    <IconPencil stroke={2} size={18} />
                  </ActionIcon>
                </Group>
              )
            }
          </Group>
        </div>
      </Group>
    </Box>
  );
};

// the laptop-2 device frames, not its 1545x978 container: no-arguments=1 drops the qr block
const PREVIEW_FRAME_W = 1523;
const PREVIEW_FRAME_H = 815;
const MODAL_Y_OFFSET = 0.05;
const MODAL_BODY_PADDING = 16;

const usePreviewFrameScale = () => {
  const ref = useRef(null);
  const [scale, setScale] = useState(null);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const availableWidth = el.clientWidth;
    // the modal leaves --modal-y-offset free below its content and pads its body
    const availableHeight =
      window.innerHeight * (1 - MODAL_Y_OFFSET) -
      el.getBoundingClientRect().top -
      MODAL_BODY_PADDING;

    if (availableWidth <= 0 || availableHeight <= 0) return;

    const next = Math.min(
      1,
      availableWidth / PREVIEW_FRAME_W,
      availableHeight / PREVIEW_FRAME_H
    );

    setScale((curr) => (curr === next ? curr : next));
  }, []);

  useLayoutEffect(measure);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return { ref, scale };
};

const TemplateCardPreview = ({ id }) => {
  const { t } = useTranslation();
  const query = queryShowcases({ id });
  const { ref: frameRef, scale } = usePreviewFrameScale();

  const previewUrl = query.data?.[0]?.ad_preview;

  if (query.isLoading) {
    return (
      <Box p={0} style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
        <Loader />
      </Box>
    );
  }

  if (query.isError) {
    return (
      <Box p="md">
        <Text c="red">{t('templates.previewFailed')}</Text>
      </Box>
    );
  }

  if (!previewUrl) {
    return (
      <Box p="md">
        <Text>{t('templates.noPreview')}</Text>
      </Box>
    );
  }

  return (
    <Box p={0}>
      <Box ref={frameRef}>
        <Box
          w={Math.round(PREVIEW_FRAME_W * (scale ?? 1))}
          h={Math.round(PREVIEW_FRAME_H * (scale ?? 1))}
          mx="auto"
          style={{ overflow: "hidden" }}
        >
          <iframe
            src={previewUrl}
            width={PREVIEW_FRAME_W}
            height={PREVIEW_FRAME_H}
            scrolling="no"
            style={{
              display: "block",
              border: "none",
              transform: `scale(${scale ?? 1})`,
              transformOrigin: "top left",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};


export const TemplateCardLoading = () => (
  <Box mah={458.8}>
    <Skeleton height={366.8} w="100%" radius="lg" />
    <Box mt="xs">
      <div>
        <Skeleton height={24} width="70%" radius="sm" />
        <Skeleton height={20} width="40%" radius="sm" mt={4} />
      </div>
    </Box>
    <Grid mt={4}>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Skeleton height={30} width="100%" radius="md" />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Skeleton height={30} width="100%" radius="md" />
      </Grid.Col>
    </Grid>
  </Box>
);

const isImageFullyWhite = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      try {
        const { data } = ctx.getImageData(0, 0, img.width, img.height);
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          if (!(r > 250 && g > 250 && b > 250 && a > 250)) {
            resolve(false);
            return;
          }
        }
        resolve(true);
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = reject;
    img.src = src;
  });


